/**
 * WhatsApp Send Logs page.
 *
 * Lists rows from companies/{id}/whatsappMessages — every send/receive.
 * Filters: date range, direction, status, doc type, party text.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { collection, query, orderBy, limit as fbLimit, getDocs } from 'firebase/firestore';
import { FiSearch, FiRefreshCw, FiInfo } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { colors } from '../../theme/colors';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';

const STATUS_COLORS = {
  sent: '#1b5e20', delivered: '#1b5e20', read: '#0d47a1',
  failed: '#b00020', queued: '#9c27b0'
};

const WhatsAppLogs = () => {
  const { activeCompany } = useAuth();
  const { canView } = usePagePermissions('whatsapp', 'logs');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [detailRow, setDetailRow] = useState(null);

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'whatsappMessages');
      const q = query(ref, orderBy('sentAt', 'desc'), fbLimit(500));
      const snap = await getDocs(q);
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [activeCompany?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return rows.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (docTypeFilter !== 'all' && r.relatedDocType !== docTypeFilter) return false;
      if (q) {
        const hay = `${r.toPhone || ''} ${r.body || ''} ${r.relatedDocNumber || ''} ${r.pdfFilename || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, docTypeFilter]);

  const docTypes = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.relatedDocType) set.add(r.relatedDocType); });
    return [...set].sort();
  }, [rows]);

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' });
  };

  if (!canView) {
    return <PageLayout pageTitle="WhatsApp Logs"><Empty>You don't have permission to view logs.</Empty></PageLayout>;
  }

  return (
    <PageLayout pageTitle="WhatsApp Logs">
      <Container>
        <Header>
          <Title><FaWhatsapp /> Send Logs</Title>
          <Subtitle>Every WhatsApp message sent from this company.</Subtitle>
        </Header>

        <Toolbar>
          <SearchBox>
            <FiSearch />
            <input type="text" placeholder="Search phone, doc#, body…" value={search} onChange={e => setSearch(e.target.value)} />
          </SearchBox>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="read">Read</option>
            <option value="failed">Failed</option>
          </Select>
          <Select value={docTypeFilter} onChange={e => setDocTypeFilter(e.target.value)}>
            <option value="all">All doc types</option>
            {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
          <RefreshBtn onClick={load} disabled={loading}><FiRefreshCw /> Refresh</RefreshBtn>
        </Toolbar>

        {loading ? <Empty>Loading…</Empty> : filtered.length === 0 ? (
          <Empty>No messages match these filters.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>To</Th>
                <Th>Type</Th>
                <Th>Doc</Th>
                <Th>Preview</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <Tr key={r.id}>
                  <Td>{fmtDate(r.sentAt)}</Td>
                  <Td><Phone>{r.toPhone}</Phone></Td>
                  <Td>{r.messageType === 'document' ? '📎 PDF' : '💬 Text'}</Td>
                  <Td>{r.relatedDocType ? `${r.relatedDocType} ${r.relatedDocNumber || ''}` : ''}</Td>
                  <Td><Preview>{r.body?.slice(0, 80)}{r.body?.length > 80 ? '…' : ''}</Preview></Td>
                  <Td><Status $color={STATUS_COLORS[r.status] || '#666'}>{r.status}</Status></Td>
                  <Td><ViewBtn onClick={() => setDetailRow(r)}><FiInfo /></ViewBtn></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}

        <Summary>Showing <b>{filtered.length}</b> of {rows.length} messages.</Summary>

        {detailRow && (
          <ModalBackdrop onClick={() => setDetailRow(null)}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalHead>
                <h3>Message Detail</h3>
                <CloseBtn onClick={() => setDetailRow(null)}>×</CloseBtn>
              </ModalHead>
              <DetailGrid>
                <DetailLabel>To</DetailLabel><DetailVal>{detailRow.toPhone}</DetailVal>
                <DetailLabel>When</DetailLabel><DetailVal>{fmtDate(detailRow.sentAt)}</DetailVal>
                <DetailLabel>Type</DetailLabel><DetailVal>{detailRow.messageType}</DetailVal>
                <DetailLabel>Doc</DetailLabel><DetailVal>{detailRow.relatedDocType ? `${detailRow.relatedDocType} ${detailRow.relatedDocNumber || ''}` : '—'}</DetailVal>
                <DetailLabel>Status</DetailLabel><DetailVal><Status $color={STATUS_COLORS[detailRow.status] || '#666'}>{detailRow.status}</Status></DetailVal>
                {detailRow.pdfFilename && <><DetailLabel>PDF</DetailLabel><DetailVal>{detailRow.pdfFilename}</DetailVal></>}
                {detailRow.waMessageId && <><DetailLabel>WA Message ID</DetailLabel><DetailVal style={{fontSize:11,fontFamily:'monospace'}}>{detailRow.waMessageId}</DetailVal></>}
                {detailRow.error && <><DetailLabel>Error</DetailLabel><DetailVal style={{color:'#b00020'}}>{detailRow.error}</DetailVal></>}
              </DetailGrid>
              <DetailLabel style={{marginTop:14}}>Body</DetailLabel>
              <Body>{detailRow.body}</Body>
            </Modal>
          </ModalBackdrop>
        )}
      </Container>
    </PageLayout>
  );
};

export default WhatsAppLogs;

const Container = styled.div`max-width: 1400px; margin: 0 auto; padding: 24px;`;
const Header = styled.div`margin-bottom: 16px;`;
const Title = styled.h1`margin: 0; font-size: 24px; font-weight: 700; color: ${colors.text.primary}; display: flex; align-items: center; gap: 10px;`;
const Subtitle = styled.div`color: ${colors.text.secondary}; font-size: 14px; margin-top: 4px;`;
const Toolbar = styled.div`display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap;`;
const SearchBox = styled.div`display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #ddd; border-radius: 6px; padding: 0 12px; flex: 1; min-width: 240px;
  input { border: none; outline: none; padding: 10px 0; font-size: 14px; flex: 1; }`;
const Select = styled.select`padding: 9px 12px; border: 1px solid #ddd; border-radius: 6px; background: #fff; font-size: 14px;`;
const RefreshBtn = styled.button`display: inline-flex; align-items: center; gap: 6px; padding: 9px 14px; background: #fff; border: 1px solid #ddd; border-radius: 6px; cursor: pointer; font-size: 14px;`;
const Table = styled.table`width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #eee; border-radius: 8px; overflow: hidden;`;
const Th = styled.th`text-align: left; padding: 10px 12px; background: #fafafa; font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; border-bottom: 1px solid #eee;`;
const Tr = styled.tr`&:hover { background: #fafafa; }`;
const Td = styled.td`padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #f0f0f0;`;
const Phone = styled.span`font-family: monospace; font-size: 12px;`;
const Preview = styled.span`color: ${colors.text.secondary}; font-size: 12px;`;
const Status = styled.span`background: ${p => p.$color}20; color: ${p => p.$color}; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: uppercase;`;
const ViewBtn = styled.button`background: transparent; border: none; cursor: pointer; color: ${colors.text.secondary}; padding: 4px; &:hover { color: #800020; }`;
const Summary = styled.div`margin-top: 12px; color: ${colors.text.secondary}; font-size: 13px;`;
const Empty = styled.div`padding: 60px; text-align: center; color: ${colors.text.secondary};`;
const ModalBackdrop = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000;`;
const Modal = styled.div`background: #fff; padding: 24px; border-radius: 10px; max-width: 600px; width: 90%; max-height: 80vh; overflow: auto;`;
const ModalHead = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;
  h3 { margin: 0; }`;
const CloseBtn = styled.button`background: none; border: none; font-size: 24px; cursor: pointer; color: ${colors.text.secondary};`;
const DetailGrid = styled.div`display: grid; grid-template-columns: 130px 1fr; gap: 8px;`;
const DetailLabel = styled.div`font-size: 12px; color: ${colors.text.secondary}; text-transform: uppercase; font-weight: 600;`;
const DetailVal = styled.div`font-size: 13px;`;
const Body = styled.div`background: #f9f9f9; padding: 12px; border-radius: 6px; white-space: pre-wrap; font-size: 13px; line-height: 1.5; margin-top: 8px;`;
